import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../services/auth';
import { 
  IconClose, 
  IconRefresh, 
  IconCamera, 
  IconDownload, 
  IconAlert,
  IconCheck
} from './Icons';

interface ScreenshotModalProps {
  instanceId: string;
  instanceName: string;
  isRunning: boolean;
  onClose: () => void;
}

export const ScreenshotModal: React.FC<ScreenshotModalProps> = ({
  instanceId,
  instanceName,
  isRunning,
  onClose
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [imageData, setImageData] = useState<string | null>(null);
  const [pageUrl, setPageUrl] = useState<string | null>(null);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [downloadSuccess, setDownloadSuccess] = useState<boolean>(false);

  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchScreenshot = useCallback(async (isBackground: boolean = false) => {
    if (!isBackground) {
      setLoading(true);
    }
    setError(null);

    try {
      const token = authService.getAuthToken();
      const res = await fetch(`/api/instances/${instanceId}/screenshot`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}: Ошибка при создании скриншота`);
      }

      const data = await res.json();
      setImageData(data.image);
      setPageUrl(data.url || null);
      setPageTitle(data.title || null);
      setLastUpdated(new Date(data.timestamp || Date.now()));
    } catch (err: any) {
      console.error('Screenshot fetch failed:', err);
      setError(err.message || 'Не удалось получить снимок экрана');
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [instanceId]);

  // Загрузка скриншота при открытии
  useEffect(() => {
    fetchScreenshot();
  }, [fetchScreenshot]);

  // Обработка автообновления
  useEffect(() => {
    if (autoRefresh && isRunning) {
      autoRefreshTimerRef.current = setInterval(() => {
        fetchScreenshot(true);
      }, 3000);
    } else {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
        autoRefreshTimerRef.current = null;
      }
    }

    return () => {
      if (autoRefreshTimerRef.current) {
        clearInterval(autoRefreshTimerRef.current);
      }
    };
  }, [autoRefresh, isRunning, fetchScreenshot]);

  const handleDownload = () => {
    if (!imageData) return;

    try {
      const a = document.createElement('a');
      a.href = imageData;
      const cleanName = (instanceName || instanceId).replace(/[^a-zA-Z0-9а-яА-Я_-]/g, '_');
      a.download = `screenshot_${cleanName}_${Date.now()}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to download image:', err);
    }
  };

  const formattedTime = lastUpdated 
    ? lastUpdated.toLocaleTimeString('ru-RU', { hour12: false }) 
    : null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div 
        className="instance-modal screenshot-modal" 
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: '1080px', width: '95%' }}
      >
        <div className="modal-header">
          <div className="modal-title-wrap">
            <div className="bot-avatar-frame" style={{ width: '38px', height: '38px' }}>
              <IconCamera size={20} />
            </div>
            <div className="modal-title-row">
              <h3>БРАУЗЕР: {instanceName.toUpperCase() || 'ИНСТАНС'}</h3>
              <span className="modal-id-badge">ID: {instanceId}</span>
              <span className={`status-badge ${isRunning ? 'running' : 'stopped'}`} style={{ marginLeft: '8px' }}>
                <span className="status-dot"></span>
                <span className="status-text">{isRunning ? 'ОНЛАЙН' : 'ОСТАНОВЛЕН'}</span>
              </span>
            </div>
          </div>
          <button type="button" className="btn-modal-close" onClick={onClose} title="Закрыть">
            <IconClose size={18} />
          </button>
        </div>

        {/* Браузерный тулбар */}
        <div className="screenshot-browser-toolbar">
          <div className="browser-url-pill" title={pageUrl || 'URL страницы'}>
            <span className="url-protocol">https://</span>
            <span className="url-text">
              {pageUrl ? pageUrl.replace(/^https?:\/\//, '') : 'web.telegram.org/k/'}
            </span>
            {pageTitle && (
              <span className="page-title-badge" title={pageTitle}>
                {pageTitle}
              </span>
            )}
          </div>

          <div className="screenshot-toolbar-actions">
            {formattedTime && (
              <span className="screenshot-timestamp" title="Время последнего снимка">
                СНИМОК: <strong>{formattedTime}</strong>
              </span>
            )}

            <label className="screenshot-auto-refresh-toggle" title="Автоматическое обновление скриншота каждые 3 секунды">
              <input 
                type="checkbox" 
                checked={autoRefresh} 
                onChange={(e) => setAutoRefresh(e.target.checked)}
                disabled={!isRunning}
              />
              <span className="toggle-label">АВТО (3С)</span>
            </label>

            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => fetchScreenshot()}
              disabled={loading}
              title="Обновить скриншот сейчас"
            >
              <IconRefresh size={13} className={loading ? 'icon-spin' : ''} />
              <span>{loading ? 'СНИМАЕМ...' : 'ОБНОВИТЬ'}</span>
            </button>

            <button
              type="button"
              className={`btn btn-sm ${downloadSuccess ? 'btn-success' : 'btn-outline'}`}
              onClick={handleDownload}
              disabled={!imageData}
              title="Скачать скриншот в JPG"
            >
              {downloadSuccess ? <IconCheck size={13} /> : <IconDownload size={13} />}
              <span>{downloadSuccess ? 'СОХРАНЕНО' : 'СКАЧАТЬ'}</span>
            </button>
          </div>
        </div>

        {/* Основная область просмотра скриншота */}
        <div className="screenshot-viewport-container">
          {loading && !imageData && (
            <div className="screenshot-loading-box">
              <div className="spinner"></div>
              <p>ПОДКЛЮЧЕНИЕ К БРАУЗЕРУ И СОЗДАНИЕ СНИМКА...</p>
              <span className="sub-hint">Puppeteer делает захват виртуального дисплея</span>
            </div>
          )}

          {error && (
            <div className="screenshot-error-box">
              <IconAlert size={28} />
              <h4>ОШИБКА ПОЛУЧЕНИЯ СКРИНШОТА</h4>
              <p>{error}</p>
              {!isRunning && (
                <p className="error-tip">
                  Бот в данный момент остановлен. Запустите бота кнопкой <strong>«ЗАПУСТИТЬ»</strong> в карточке, чтобы открыть браузер.
                </p>
              )}
              <button 
                type="button" 
                className="btn btn-primary btn-sm"
                onClick={() => fetchScreenshot()}
              >
                ПОВТОРИТЬ ПОПЫТКУ
              </button>
            </div>
          )}

          {!error && imageData && (
            <div className="screenshot-image-frame">
              <img 
                src={imageData} 
                alt={`Скриншот браузера ${instanceName || instanceId}`} 
                className="screenshot-img"
              />
            </div>
          )}
        </div>

        <div className="screenshot-footer">
          <span className="screenshot-footer-info">
            Разрешение экрана: 1920×1080 | Формат: JPEG | Дисплей: :99
          </span>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            ЗАКРЫТЬ
          </button>
        </div>
      </div>
    </div>
  );
};
